import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@bloomcode/ui';
import { useCookieSession } from '@bloomcode/auth';
import { 
  Ticket, 
  MessageSquare, 
  User, 
  Shield, 
  Clock, 
  CheckCircle,
  ArrowRight
} from '@bloomcode/icons';

export default function HomePage() {
  const { isAuthenticated } = useCookieSession();

  return (
    <>
      <Helmet>
        <title>Bloomcode Customer Portal - Manage Your Account</title>
        <meta name="description" content="Access your Bloomcode customer portal to manage support tickets, view communications, and update your profile." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">
              Your Customer Portal
            </h1>
            <p className="mt-6 text-xl text-muted-foreground">
              Manage your account, get support, and stay connected with Bloomcode. 
              Everything you need in one place.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button size="lg" className="w-full sm:w-auto gap-2">
                    Go to Dashboard
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth/register">
                    <Button size="lg" className="w-full sm:w-auto gap-2">
                      Create Account
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/auth/login">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      Sign In to Your Account
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">What You Can Do</h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              Access all the tools you need to manage your relationship with us
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="p-6 bg-card rounded-xl border shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Ticket className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Support Tickets</h3>
              <p className="text-muted-foreground">
                Submit and track support requests. Get help from our team and view your ticket history.
              </p>
            </div>
            <div className="p-6 bg-card rounded-xl border shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Communications</h3>
              <p className="text-muted-foreground">
                View all your communications with us in one place. Never miss an important update.
              </p>
            </div>
            <div className="p-6 bg-card rounded-xl border shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <User className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Profile Management</h3>
              <p className="text-muted-foreground">
                Keep your contact information up to date and manage your notification preferences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">Why Use the Customer Portal?</h2>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-success" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">24/7 Access</h3>
                      <p className="text-muted-foreground">
                        Access your account anytime, anywhere. No need to wait for business hours.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Shield className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">Secure & Private</h3>
                      <p className="text-muted-foreground">
                        Your data is protected with enterprise-grade security and encryption.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-accent" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">Track Everything</h3>
                      <p className="text-muted-foreground">
                        Keep track of all your interactions, tickets, and communications in one place.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-xl border p-8 shadow-lg">
                <h3 className="text-xl font-semibold mb-4">Ready to get started?</h3>
                <p className="text-muted-foreground mb-6">
                  {isAuthenticated
                    ? 'Head to your dashboard to manage your account.'
                    : 'Create your account in minutes and start managing your relationship with us more effectively.'}
                </p>
                <div className="space-y-3">
                  {isAuthenticated ? (
                    <Link to="/dashboard" className="block">
                      <Button className="w-full" size="lg">
                        Go to Dashboard
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link to="/auth/register" className="block">
                        <Button className="w-full" size="lg">
                          Create Your Account
                        </Button>
                      </Link>
                      <Link to="/auth/login" className="block">
                        <Button variant="outline" className="w-full" size="lg">
                          Already have an account? Sign In
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Need Help?</h2>
            <p className="text-muted-foreground mb-8">
              Our support team is here to help you get the most out of your customer portal.
            </p>
            <Link to="/auth/login">
              <Button variant="outline" size="lg">
                Contact Support
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
